import { Subject } from 'rxjs';

import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { DialogContainerComponent } from './dialog-container.component';
import { CpDialogConfig, CP_DIALOG_DEFAULT_CONFIG } from './dialog.config';
import { DialogInjector } from './dialog.injector';
import { CpDialogModule } from './dialog.module';
import { CpDialogRef } from './dialog.ref';
import { take } from 'rxjs/operators';

/** Injection token that can be used to access the data that was passed in to a dialog. */
export const CP_DIALOG_DATA = new InjectionToken<any>('CpDialogData');

export type TokenKey<D> = InjectionToken<D> | typeof CpDialogRef;
export type TokenValue<T, D, R> = D | CpDialogRef<T, R>;

/**
 * Notes:
 *
 * `T`: the component you use when opening the modal
 *
 * `D`: configuration information that you provide when opening the modal
 *
 * `R`: type of the data that is expected to be returned after you click the confirmation button (save, continue ...)
 *
 * You get this through `afterClosed` subscription (i.e. `dialogRef.afterClosed().subscribe((data: R) => {  }))`
 */
@Injectable({
  providedIn: CpDialogModule,
})
export class CpDialog {
  private currentDialogComponentRef: unknown;
  private readonly _afterAllClosed = new Subject<void>();
  public readonly afterAllClosed = this._afterAllClosed.asObservable();
  private readonly _afterAnyOpen = new Subject<unknown>();
  public readonly afterAnyOpen = this._afterAnyOpen.asObservable();
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  private attachDialogContent<T, D, R>(
    dialogRef: CpDialogRef<T, R>,
    componentType: Type<T>,
    config: CpDialogConfig<D>
  ): ComponentRef<DialogContainerComponent<T, D, R>> {
    const injectionTokens = new WeakMap<TokenKey<D>, TokenValue<T, D, R>>([
      [CP_DIALOG_DATA, config.data],
      [CpDialogRef, dialogRef],
    ]);

    // get factory for dialog component
    const componentFactory =
      this.componentFactoryResolver.resolveComponentFactory<DialogContainerComponent<T, D, R>>(
        DialogContainerComponent
      );

    // create component using factory
    const componentRef = componentFactory.create(new DialogInjector(this.injector, injectionTokens));

    const componentInstance = componentRef.instance;
    componentInstance.childComponentType = componentType;
    componentInstance.config = config;

    // attach component to Angular component tree
    this.appRef.attachView(componentRef.hostView);

    // attach component DOM to HTML DOM
    const domElem = (componentRef.hostView as EmbeddedViewRef<void>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    return componentRef;
  }

  private removeDialogContent<T, D, R>(dialogComponentRef: ComponentRef<DialogContainerComponent<T, D, R>>) {
    this.appRef.detachView(dialogComponentRef.hostView);
    dialogComponentRef.destroy();
  }

  /**
   * `T`: the component you use when opening the modal
   *
   * `D`: configuration information that you provide when opening the modal
   *
   * `R`: type of the data that is expected to be returned after you click the confirmation button (save, continue ...)
   *
   * You get this through `afterClosed` subscription (i.e. `dialogRef.afterClosed().subscribe((data: R) => {  }))`
   *
   * checkExisting: helps in preventing one modal to open on top of another
   */
  dialogRef = null;

  open<T, D, R = any>(componentType: Type<T>, config?: CpDialogConfig<D>, checkExisting = false): CpDialogRef<T, R> {
    if (checkExisting && this.currentDialogComponentRef) {
      return this.dialogRef;
    }

    this.dialogRef = new CpDialogRef<T, R>();

    const dialogComponentRef = this.attachDialogContent(
      this.dialogRef,
      componentType,
      config ? { ...CP_DIALOG_DEFAULT_CONFIG, ...config } : CP_DIALOG_DEFAULT_CONFIG
    );

    this.currentDialogComponentRef = dialogComponentRef;
    this.notifyAnyOpen<T, R>(this.dialogRef);

    // we want to know when somebody called the close method
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.removeDialogContent(dialogComponentRef);

        // We are assuming one dialog box can be open at a time
        // If we add a scenario where multiple dialog boxes can be open at the same time, then
        // we will have to turn `this.currentDialogComponentRef` into an array, and then
        // call `notifyAllClosed` after all dialog boxes have been closed
        this.currentDialogComponentRef = null;
        this.dialogRef = null;
        this.notifyAllClosed();
      });

    return this.dialogRef;
  }

  /**
   * @return true when we have an open modal
   */
  isOpen(): boolean {
    return Boolean(this.currentDialogComponentRef);
  }

  private notifyAnyOpen<T, R>(ref: CpDialogRef<T, R>): void {
    this._afterAnyOpen.next(ref);
  }
  private notifyAllClosed(): void {
    this._afterAllClosed.next();
  }
}
